import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1578670812003-3aa50e1c6a55?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1170&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #004744;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const NumberedList = styled.ol`
  margin: 2rem 0;
  padding-left: 1.5rem;
`;

const NumberedItem = styled.li`
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const CTASection = styled.div`
  margin-top: 4rem;
  text-align: center;
`;

const FidatyCard: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Fidaty Card | J-O-H' : 'Loyalty Card | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri la Fidaty Card di J-O-H, il programma fedeltà che premia i nostri clienti con vantaggi esclusivi' 
            : 'Discover the J-O-H Loyalty Card, the loyalty program that rewards our customers with exclusive benefits'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/card">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>{language === 'it' ? 'FIDATY CARD' : 'LOYALTY CARD'}</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'La carta fiducia, o carta fedeltà, è una carta collegata a un programma di fidelizzazione, in cui i clienti accumulano punti o ricevono sconti in cambio di acquisti ripetuti o di una certa quantità di spesa. I clienti possono accumulare punti o ottenere sconti speciali utilizzando la carta per gli acquisti o partecipando ad attività specifiche.'
              : 'The trust card, or loyalty card, is a card linked to a loyalty program, in which customers accumulate points or receive discounts in exchange for repeated purchases or a certain amount of spending. Customers can accumulate points or get special discounts by using the card for purchases or participating in specific activities.'}
          </Description>
          
          <NumberedList>
            <NumberedItem>
              <strong>{language === 'it' ? 'PROGRAMMA DI FIDELIZZAZIONE' : 'LOYALTY PROGRAM'}</strong>: 
              {language === 'it' 
                ? ' La carta fiducia è il mezzo principale attraverso il quale i clienti possono partecipare a un programma di fidelizzazione. Questo programma mira a premiare la fedeltà del cliente con benefici speciali.'
                : ' The trust card is the primary means through which customers can participate in a loyalty program. This program aims to reward customer loyalty with special benefits.'}
            </NumberedItem>
            <NumberedItem>
              <strong>{language === 'it' ? 'ACCUMULO PUNTI' : 'ACCUMULATE POINTS'}</strong>: 
              {language === 'it' 
                ? ' I clienti utilizzano la carta fiducia per accumulare punti ogni volta che effettuano acquisti di prodotti o servizi presso il negozio J-O-H Questi punti possono essere successivamente riscattati per sconti, prodotti gratuiti o altri benefici.'
                : ' Customers use the trust card to accumulate points every time they make purchases of products or services at the J-O-H store. These points can later be redeemed for discounts, free products or other benefits.'}
            </NumberedItem>
            <NumberedItem>
              <strong>{language === 'it' ? 'SCONTI ESCLUSIVI' : 'EXCLUSIVE DISCOUNTS'}</strong>: 
              {language === 'it' 
                ? ' I titolari della carta fiducia possono avere accesso a sconti esclusivi, offerte speciali o promozioni riservate ai membri del programma di fidelizzazione.'
                : ' Fidaty card holders can have access to exclusive discounts, special offers or promotions reserved for loyalty program members.'}
            </NumberedItem>
            <NumberedItem>
              <strong>{language === 'it' ? 'PARTECIPAZIONE AD EVENTI SPECIALI' : 'PARTICIPATION IN SPECIAL EVENTS'}</strong>: 
              {language === 'it' 
                ? ' In alcuni casi, i titolari della carta fiducia possono essere invitati a eventi speciali, anteprime di prodotti o altre iniziative riservate ai membri del programma di fidelizzazione.'
                : ' In some cases, trust card holders may be invited to special events, product previews or other initiatives reserved for loyalty program members.'}
            </NumberedItem>
            <NumberedItem>
              <strong>{language === 'it' ? 'RACCOLTA DATI' : 'DATA COLLECTION'}</strong>: 
              {language === 'it' 
                ? ' L\'utilizzo della carta fiducia permette a J-O-H di raccogliere dati sul comportamento di acquisto dei clienti. Queste informazioni possono essere utilizzate per personalizzare offerte e promozioni, migliorando l\'esperienza complessiva del cliente.'
                : ' The use of the trust card allows J-O-H to collect data on customer purchasing behaviour. This information can be used to personalize offers and promotions, improving the overall customer experience.'}
            </NumberedItem>
            <NumberedItem>
              <strong>{language === 'it' ? 'PERSONALIZZAZIONE DELLE OFFERTE' : 'CUSTOMIZATION OF OFFERS'}</strong>: 
              {language === 'it' 
                ? ' Sulla base dei dati raccolti attraverso la carta fiducia, J-O-H può offrire promozioni personalizzate che rispondono alle esigenze specifiche dei singoli clienti, aumentando ulteriormente la loro soddisfazione.'
                : ' Based on the data collected through the trust card, J-O-H can offer personalized promotions that respond to the specific needs of individual customers, further increasing their satisfaction.'}
            </NumberedItem>
            <NumberedItem>
              <strong>{language === 'it' ? 'FACILITÀ D\'USO' : 'EASE OF USE'}</strong>: 
              {language === 'it' 
                ? ' La carta fiducia è facile da utilizzare durante gli acquisti, in quanto può essere presentata alla cassa o inserita online per ottenere i benefici del programma di fidelizzazione.'
                : ' The trust card is easy to use during purchases, as it can be presented at the cash register or inserted online to obtain the benefits of the loyalty program.'}
            </NumberedItem>
            <NumberedItem>
              <strong>{language === 'it' ? 'ESPERIENZA CLIENTE MIGLIORATA' : 'ENHANCED CUSTOMER EXPERIENCE'}</strong>: 
              {language === 'it' 
                ? ' Nel complesso, la carta fiducia mira a migliorare l\'esperienza del cliente premiando la fedeltà e fornendo benefici in vari aspetti.'
                : ' Overall, the trust card aims to improve the customer experience by rewarding loyalty and providing benefits in various aspects.'}
            </NumberedItem>
          </NumberedList>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Contattaci per maggiori informazioni' : 'Contact us for more information'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default FidatyCard; 